"use client";

import { ChatList } from "@/components/AI/chat/ChatList";
import { ChatPanel } from "@/components/AI/chat/ChatPanel";
import {
  getAiMessageFromErrors,
  useJoinedErrors,
} from "@/components/Canvas/hooks/useStoryboardErrors";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { clearAllInternalErrors } from "@/store/iframeSlice";
import {
  useAiActionStore,
  useCurrentConvo,
} from "@/store/zustand/aiActionStore";
import {
  useAiContextStore,
  useCurrentMessagesBeingShown,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { Message, useSupabaseStore } from "@/store/zustand/supabaseStore";
import { track } from "@/utils/events/tracker";
import { countSearchMatches, createSearchRegex } from "@/utils/searchUtils";
import { useCanvasId } from "@/utils/utils";
import { HistoryIcon, PlusIcon, SearchIcon, XIcon } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import TopUsageIndicator from "./TopUsageIndicator";
import { useSendMessage } from "./useSendMessage";

export interface ChatProps extends React.ComponentProps<"div"> {}

export const Chat = React.memo(({ className }: ChatProps) => {
  const canvasId = useCanvasId();
  const resetMessages = useSupabaseStore((state) => state.resetMessages);
  const location = useLocation();

  const isHomeRoute = location.pathname === "/";

  const dispatch = useDispatch();

  const { conversation, messages: messagesFromConvo } = useCurrentConvo();
  const selectedConversationId = useAiActionStore(
    (state) => state.selectedConversationId
  );

  // Ai action store
  const setSelectionConversationId = useAiActionStore(
    (state) => state.setSelectionConversationId
  );

  const chatOnlyMode = useAiContextStore((state) => state.chatOnlyMode);
  const useReasoning = useAiContextStore((state) => state.useReasoning);
  const useSearch = useAiContextStore((state) => state.useSearch);

  // Triggering sending message from anywhere in the app
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage
  );

  const sendChatMessage = useAiContextStore((state) => state.sendChatMessage);
  const sendChatMessageFiles = useAiContextStore(
    (state) => state.sendChatMessageFiles
  );
  const refreshAfterResponse = useAiContextStore(
    (state) => state.refreshAfterResponse
  );
  const includeErrorsIfAny = useAiContextStore(
    (state) => state.includeErrorsIfAny
  );
  const isFreePromptSendMessage = useAiContextStore(
    (state) => state.isFreePrompt
  );

  const reasoningModel = useAiActionStore((state) => state.reasoningModel);
  const codeGenerationModel = useAiActionStore(
    (state) => state.codeGenerationModel
  );
  const agentChoice = useAiActionStore((state) => state.agent);

  // Add to DB
  const manualMessageToAdd = useAiContextStore(
    (state) => state.manualMessageToAdd
  );
  const setAddManualMessage = useAiContextStore(
    (state) => state.setAddManualMessage
  );

  const clearStoryboardIdToErrors = useDevServerStore(
    (state) => state.clearStoryboardIdToErrors
  );
  const clearTerminalErrors = useDevServerStore(
    (state) => state.clearTerminalErrors
  );

  const conversations = useSupabaseStore((state) => state.aiChatConversations);
  const conversationsStatus = useSupabaseStore(
    (state) => state.aiChatConversationsStatus
  );

  // Manually adding an assistant message
  // Temporary - while streaming in
  const tempAssistantMessage = useAiContextStore(
    (state) => state.tempAssistantMessage
  );

  const allMessagesToShow = useCurrentMessagesBeingShown();

  const { supabaseClient } = useSupabase();

  const allAiMessages = useSupabaseStore((state) => state.aiChatMessages);
  const conversationsListForCanvas = useMemo(() => {
    return Object.values(conversations)
      .filter((c) => c.canvas_id === canvasId)
      .map((c) => {
        const lastMessage = Object.values(allAiMessages)
          .filter((m) => m.conversation_id === c.id)
          .sort((a: any, b: any) => {
            return (
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
            );
          })[0];

        return {
          ...c,
          lastMessage: lastMessage || {
            created_at: c.created_at,
            content: "",
          },
        };
      })
      .sort((a, b) => {
        return (
          new Date(b.lastMessage?.created_at).getTime() -
          new Date(a.lastMessage?.created_at).getTime()
        );
      });
  }, [conversations, allAiMessages]);

  const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);

  const allErrors = useJoinedErrors();

  // Use useRef instead of useState to avoid unnecessary rerenders
  // and potential race conditions with Set modifications
  const errorsLoggedRef = useRef(new Set<string>());

  useEffect(() => {
    if (selectedConversationId) {
      resetMessages(selectedConversationId);
    }
  }, [selectedConversationId]);

  // Reset the errors logged when the errors are cleared
  useEffect(() => {
    if (!allErrors?.length) {
      errorsLoggedRef.current.clear();
    }
  }, [allErrors]);

  // Log all errors in posthog, but only when the error messages/stack change
  const uniquenessCheck = useMemo(() => {
    return allErrors.map((error) => error.message + error.stack).join(",");
  }, [allErrors]);

  useEffect(() => {
    if (allErrors?.length) {
      allErrors.forEach((error) => {
        const errorCheck = error.message + error.stack;
        if (errorsLoggedRef.current.has(errorCheck)) {
          return;
        }

        errorsLoggedRef.current.add(errorCheck);
        track("ERROR_IN_CHAT", {
          error: error,
        });
      });
    }
  }, [uniquenessCheck]);

  const createNewConvo = async () => {
    if (!selectedConversationId || !allMessagesToShow?.length) {
      return;
    }

    const newConversation = {
      id: uuidv4().toString(),
      canvas_id: canvasId,
      created_at: new Date().toISOString(),
    };

    onDbSnapshot("aiChatConversations", {
      newItems: [newConversation],
    });

    // @ts-expect-error
    await supabaseClient
      .from("ai_chat_conversations")
      .insert([newConversation]);

    setSelectionConversationId(newConversation.id);
  };

  useEffect(() => {
    const addMessageToDb = async () => {
      let fullTextContent = manualMessageToAdd?.text || "";
      if (manualMessageToAdd?.progressItems?.length) {
        fullTextContent +=
          '\n<action-progress>\n{"items":' +
          JSON.stringify(manualMessageToAdd?.progressItems) +
          "}\n</action-progress>";
      }

      if (manualMessageToAdd?.afterText) {
        fullTextContent += `\n${manualMessageToAdd?.afterText}`;
      }

      const newMessage: any = {
        id: manualMessageToAdd.id || uuidv4().toString(),
        conversation_id: selectedConversationId,
        canvas_id: canvasId,
        content: fullTextContent,
        role: "assistant",
        message_context: null,
        tool_calls: null,
        tool_call_id: null,
        created_at: new Date().toISOString(),
      };

      onDbSnapshot("aiChatMessages", {
        newItems: [newMessage],
      });

      // @ts-expect-error
      await supabaseClient.from("ai_chat_messages").insert(newMessage);
    };

    if (manualMessageToAdd && selectedConversationId) {
      // @ts-expect-error TS2345
      setAddManualMessage(null);
      addMessageToDb();
    }
  }, [manualMessageToAdd, selectedConversationId]);

  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchInput, setShowSearchInput] = useState(false);

  // Filter messages based on search query
  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return allMessagesToShow;

    // Use the createSearchRegex utility function
    const { regex } = createSearchRegex(searchQuery) || { regex: null };
    if (!regex) return allMessagesToShow;

    const filtered =
      allMessagesToShow?.filter((message) => {
        if (!message?.content) return false;
        // Test if the message contains the search query
        return regex.test(message.content);
      }) || [];

    return filtered;
  }, [allMessagesToShow, searchQuery]);

  // Reset search when changing conversations
  useEffect(() => {
    setSearchQuery("");
    setShowSearchInput(false);
  }, [conversation?.id]);

  /**
   * If there are no conversations, create a new one
   */
  useEffect(() => {
    if (isHomeRoute || !supabaseClient) {
      return;
    }

    if (
      !conversationsListForCanvas?.length &&
      conversationsStatus === "succeeded"
    ) {
      // Create a new conversation
      const createConvo = async () => {
        const newConversation = {
          id: uuidv4(),
          canvas_id: canvasId,
          created_at: new Date().toISOString(), // Add explicit timestamp
        };

        // Double check there are no conversations
        const { data: existingConversations, error } = await supabaseClient
          .from("ai_chat_conversations")
          .select("id")
          .eq("canvas_id", canvasId);

        if (error || existingConversations?.length) {
          return;
        }

        await supabaseClient
          .from("ai_chat_conversations")
          .insert([newConversation]);

        onDbSnapshot("aiChatConversations", {
          newItems: [newConversation],
        });
      };

      createConvo();
    }
  }, [conversationsListForCanvas, conversationsStatus, supabaseClient]);

  /**
   * Select the last conversation on first load
   */
  useEffect(() => {
    if (!selectedConversationId && conversationsListForCanvas?.length) {
      setSelectionConversationId(conversationsListForCanvas[0].id);
    }
  }, [conversationsListForCanvas, selectedConversationId]);

  const isChatLoading = useIsChatLoading();

  const { sendMessage } = useSendMessage();

  useEffect(() => {
    if (!conversation) return;

    if (sendChatMessage) {
      setSendChatMessage({
        // @ts-expect-error TS2322
        sendChatMessage: null,
      });

      const messagesCanSummarize: Message[] = [...(messagesFromConvo || [])];
      const messagesDoNotSummarize: Message[] = [];

      if (includeErrorsIfAny && allErrors?.length) {
        const errorsMessage = getAiMessageFromErrors(allErrors);

        const errorMessage: any = {
          id: uuidv4().toString(),
          conversation_id: selectedConversationId,
          canvas_id: canvasId,
          content: errorsMessage.content,
          role: "user",
          message_context: null,
          tool_calls: null,
          tool_call_id: null,
          created_at: new Date().toISOString(),
        };

        onDbSnapshot("aiChatMessages", {
          newItems: [errorMessage],
        });

        const addToDb = async () => {
          // @ts-expect-error
          await supabaseClient.from("ai_chat_messages").insert(errorMessage);
        };

        addToDb();

        messagesDoNotSummarize.push(errorMessage);

        clearStoryboardIdToErrors();
        dispatch(clearAllInternalErrors());
        clearTerminalErrors();
      }

      sendMessage({
        userPrompt: sendChatMessage,
        sendChatMessageFiles,
        refreshAfterResponse,
        disableTextEditor: false,
        messagesCanSummarize,
        messagesDoNotSummarize,
        criticalFiles: [],
        chatOnlyMode: chatOnlyMode,
        isFreePrompt: isFreePromptSendMessage,
        useReasoning: useReasoning,
        useSearch: useSearch,
        reasoningModel: reasoningModel,
        codeGenerationModel: codeGenerationModel,
        aiAgent: agentChoice,
      });
    }
  }, [sendChatMessage, conversation]);

  const searchResultText = useMemo(() => {
    if (!searchQuery) return ""; // Return empty if no search query
    const totalMatches = countSearchMatches(filteredMessages, searchQuery);
    return `Found ${totalMatches} matches in ${
      filteredMessages.length
    } message${filteredMessages.length !== 1 ? "s" : ""}`;
  }, [filteredMessages, searchQuery]);

  return (
    <div className={cn("relative h-full flex flex-col max-h-full", className)}>
      <div className="flex flex-row items-center p-3 pl-2 h-12 border-b">
        <div className="flex flex-row grow items-center justify-start">
          <TopUsageIndicator />
        </div>
        <div className="flex gap-4 flex-row items-center text-xs relative">
          <div
            className={`right-full mr-1 ${
              showSearchInput ? "opacity-100" : "opacity-0 pointer-events-none"
            } transition-opacity duration-150`}
          >
            <div className="flex items-center">
              <Input
                type="text"
                className="h-6 pl-2 pr-6 rounded border border-border-primary text-xs w-32 focus:outline-none focus:ring-1 focus:ring-accent"
                placeholder="Search chat..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus={showSearchInput}
              />
              {searchQuery && (
                <XIcon
                  className="size-3 ml-2 right-2 text-text-tertiary hover:text-text-secondary cursor-pointer"
                  onClick={() => setSearchQuery("")}
                />
              )}
            </div>
          </div>
          <SearchIcon
            className={cn(
              "size-4 hover:text-text-secondary text-text-muted cursor-pointer",
              isHomeRoute && "hidden",
              isChatLoading && "opacity-50 pointer-events-none cursor-default",
              showSearchInput && "text-accent"
            )}
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content="Search Chat"
            onClick={() => setShowSearchInput(!showSearchInput)}
          />
          <PlusIcon
            className={cn(
              "size-4 hover:text-text-secondary text-text-muted cursor-pointer",
              isHomeRoute && "hidden",
              isChatLoading && "opacity-50 pointer-events-none cursor-default"
            )}
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content="New Chat"
            onClick={createNewConvo}
          />
          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              className={cn(isHomeRoute && "hidden")}
              disabled={isChatLoading}
            >
              <HistoryIcon
                className={cn(
                  "size-4 hover:text-text-secondary text-text-muted cursor-pointer",
                  isChatLoading &&
                    "opacity-50 pointer-events-none cursor-default"
                )}
                data-tooltip-id="tooltip"
                data-tooltip-place="bottom"
                data-tooltip-content="Chat History"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 mt-8" side={"right"}>
              <DropdownMenuLabel>History</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup className="w-full max-h-40 overflow-y-auto">
                {conversationsListForCanvas.map((conversation) => {
                  // Check if the timestamp is valid before trying to format it
                  const timestamp = conversation.created_at;
                  let formattedDate = "New conversation";

                  if (timestamp) {
                    try {
                      const date = new Date(timestamp);

                      // Make sure the date is valid
                      if (!isNaN(date.getTime())) {
                        const optionsDate: Intl.DateTimeFormatOptions = {
                          month: "short",
                          day: "numeric",
                        };
                        const optionsTime: Intl.DateTimeFormatOptions = {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        };
                        formattedDate = `${date.toLocaleDateString(
                          "en-US",
                          optionsDate
                        )}, ${date.toLocaleTimeString("en-US", optionsTime)}`;
                      }
                    } catch (e) {
                      console.error("Error formatting date:", e);
                    }
                  }

                  return (
                    <DropdownMenuItem
                      key={conversation.id}
                      onClick={() => {
                        setSelectionConversationId(conversation.id);
                      }}
                    >
                      <div className="flex flex-row items-center gap-2 w-full">
                        <div className="shrink-0">{formattedDate}</div>
                        <div className="truncate text-secondary-800 min-w-0 w-0 grow">
                          {conversation.lastMessage?.content}
                        </div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className={cn("grow min-h-0")}>
        {allMessagesToShow?.length ? (
          <>
            {searchQuery && (
              <div className="text-xs text-text-secondary px-4 py-2 bg-bg-secondary border-b border-border-primary">
                {filteredMessages.length === 0
                  ? "No messages found"
                  : searchResultText}
              </div>
            )}
            {(!searchQuery || filteredMessages.length > 0) && (
              <ChatList
                messages={searchQuery ? filteredMessages : allMessagesToShow}
                conversationId={conversation?.id}
                searchQuery={searchQuery}
              />
            )}
          </>
        ) : null}
      </div>
      <div className="relative">
        <ChatPanel />
      </div>
    </div>
  );
});
Chat.displayName = "Chat";
